import React, { useState } from 'react';

import { AddCircle, Close } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField } from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { ClientClient, CreateClientCommand } from '../../../api/api';
import RedirectUrlPlaceholderHint from './RedirectUrlPlaceholderHint';

interface CreateClientDialogProps {
	open: boolean;
	onClose: () => void;
	onCreated: (clientId: string) => void;
}

export default function CreateClientDialog({ open, onClose, onCreated }: CreateClientDialogProps) {
	const client = useApiClient(ClientClient) as ClientClient;
	const toastService = useToastService();
	const [name, setName] = useState('');
	const [code, setCode] = useState('');
	const [description, setDescription] = useState('');
	const [redirectUrls, setRedirectUrls] = useState<string[]>(['']);
	const [isSaving, setIsSaving] = useState(false);

	const reset = () => {
		setName('');
		setCode('');
		setDescription('');
		setRedirectUrls(['']);
	};

	const handleClose = () => {
		if (isSaving) return;
		reset();
		onClose();
	};

	const handleCreate = async () => {
		if (!name.trim() || !code.trim()) return;

		setIsSaving(true);
		try {
			const normalizedRedirectUrls = normalizeRedirectUrls(redirectUrls);
			const request: CreateClientCommand = {
				name: name.trim(),
				code: code.trim(),
				description: description.trim() || undefined,
				redirectUrls: normalizedRedirectUrls.length > 0 ? normalizedRedirectUrls : undefined,
			};
			const clientId = await client.create(request);
			toastService.success(`Client "${request.name}" created successfully.`);
			reset();
			onCreated(clientId);
		} catch (error) {
			console.error('Error creating client:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to create client: ${errorMessage}`);
		} finally {
			setIsSaving(false);
		}
	};

	const handleChangeRedirectUrl = (index: number, value: string) => {
		setRedirectUrls((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
	};

	const handleAddRedirectUrl = () => {
		setRedirectUrls((current) => [...current, '']);
	};

	const handleRemoveRedirectUrl = (index: number) => {
		setRedirectUrls((current) => {
			const next = current.filter((_, itemIndex) => itemIndex !== index);
			return next.length > 0 ? next : [''];
		});
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>Create Client</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} autoFocus required fullWidth />
					<TextField label="Code" value={code} onChange={(event) => setCode(event.target.value)} required fullWidth />
					<TextField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={3} fullWidth />
					<Stack spacing={1}>
						{redirectUrls.map((redirectUrl, index) => (
							<Stack key={index} direction="row" spacing={1} alignItems="center">
								<TextField
									label={index === 0 ? 'Redirect URI Templates' : undefined}
									value={redirectUrl}
									onChange={(event) => handleChangeRedirectUrl(index, event.target.value)}
									placeholder="https://{HostName}/callback"
									size="small"
									fullWidth
								/>
								<IconButton size="small" onClick={() => handleRemoveRedirectUrl(index)} sx={{ p: 0.5 }}>
									<Close sx={{ fontSize: 16 }} />
								</IconButton>
							</Stack>
						))}
						<Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={handleAddRedirectUrl} sx={{ alignSelf: 'flex-start' }}>
							Add URI
						</Button>
						<RedirectUrlPlaceholderHint />
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button onClick={handleCreate} disabled={isSaving || !name.trim() || !code.trim()} variant="contained">
					{isSaving ? 'Creating...' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function normalizeRedirectUrls(redirectUrls: string[]): string[] {
	return Array.from(new Set(redirectUrls.map((item) => item.trim()).filter(Boolean))).sort((left, right) => left.localeCompare(right));
}
